// Documentazione API — sezioni "IntegrationsDocs". JSX estratto verbatim da DeveloperSettings.tsx.
import type { ReactElement } from 'react';
import { AlertCircle } from 'lucide-react';
import { getPublicBaseUrl } from '@/lib/api';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-frontend/components/ui/table';
import { DocSection, Endpoint, CodeBlock } from './primitives';

export function renderIntegrationsDocSection(section: string): ReactElement | null {
  switch (section) {
      case 'mcp-booking-otp':
        return (
          <DocSection id="mcp-booking-otp">
            <h3 className="text-lg font-semibold">Booking OTP via WhatsApp</h3>
            <p className="text-sm text-muted-foreground">
              Verifica l'identità dei nuovi clienti prima della creazione dell'appuntamento tramite un codice OTP a 6 cifre inviato via WhatsApp con un template Twilio approvato.
              Usato per ridurre prenotazioni fake/spam su chatbot web e voicebot inbound.
            </p>

            <h4 className="text-sm font-semibold mt-4">Abilitazione</h4>
            <p className="text-sm text-muted-foreground">
              Configurabile da Impostazioni → <strong>Sicurezza prenotazioni</strong>. Opt-in indipendente per <code className="bg-muted px-1 rounded">chatbot_web</code> e <code className="bg-muted px-1 rounded">voicebot_inbound</code>. Richiede la selezione di un template WhatsApp approvato con una variabile <code className="bg-muted px-1 rounded">{'{{1}}'}</code> per il codice.
            </p>

            <h4 className="text-sm font-semibold mt-4">Limiti di sicurezza</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Parametro</TableHead><TableHead>Valore</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell>Lunghezza codice</TableCell><TableCell>6 cifre numeriche</TableCell></TableRow>
                  <TableRow><TableCell>Scadenza</TableCell><TableCell>5 minuti</TableCell></TableRow>
                  <TableRow><TableCell>Tentativi di verifica</TableCell><TableCell>max 3 per codice</TableCell></TableRow>
                  <TableRow><TableCell>Rate limit richieste</TableCell><TableCell>3 per numero/ora</TableCell></TableRow>
                  <TableRow><TableCell>Blocco dopo 3 fallimenti</TableCell><TableCell>15 minuti</TableCell></TableRow>
                  <TableRow><TableCell>TTL otp_token (JWT)</TableCell><TableCell>10 minuti</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">MCP Tool: request_customer_otp</h4>
            <p className="text-sm text-muted-foreground">Invia un codice OTP al numero del cliente tramite WhatsApp.</p>
            <CodeBlock title="Input Schema">{`{
  "phone": "+393331234567",
  "source": "chatbot_web" | "voicebot_inbound",
  "customer_name": "Mario Rossi"   // opzionale
}`}</CodeBlock>
            <CodeBlock title="Response">{`{
  "success": true,
  "otp_sent": true,
  "expires_in_seconds": 300,
  "message": "Codice inviato via WhatsApp"
}`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">MCP Tool: verify_customer_otp</h4>
            <p className="text-sm text-muted-foreground">Verifica il codice e restituisce un <code className="bg-muted px-1 rounded">otp_token</code> (JWT) da passare a <code className="bg-muted px-1 rounded">create_appointment</code>.</p>
            <CodeBlock title="Input Schema">{`{
  "phone": "+393331234567",
  "code": "123456",
  "source": "chatbot_web" | "voicebot_inbound"
}`}</CodeBlock>
            <CodeBlock title="Response">{`{
  "success": true,
  "verified": true,
  "otp_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
}`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Integrazione con create_appointment</h4>
            <p className="text-sm text-muted-foreground">
              Quando l'OTP è abilitato per la sorgente, <code className="bg-muted px-1 rounded">create_appointment</code> accetta i campi <code className="bg-muted px-1 rounded">source</code> e <code className="bg-muted px-1 rounded">otp_token</code>. Il token viene validato prima di creare l'appuntamento; in caso di mancanza o scadenza la chiamata ritorna un errore con codice <code className="bg-muted px-1 rounded">OTP_REQUIRED</code> / <code className="bg-muted px-1 rounded">OTP_INVALID</code>.
            </p>
            <CodeBlock title="create_appointment — nuovi campi">{`{
  "customer_phone": "+393331234567",
  "source": "chatbot_web",
  "otp_token": "eyJhbGciOiJIUzI1NiIs...",
  "start_at": "2026-05-01T10:00:00Z",
  "duration_minutes": 30
}`}</CodeBlock>

            <Alert className="mt-2">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Bypass quando l'impostazione è OFF</AlertTitle>
              <AlertDescription className="text-xs">
                Se il tenant non ha abilitato l'OTP per la sorgente, <code className="bg-muted px-1 rounded">otp_token</code> è ignorato e l'appuntamento viene creato senza verifica. Clienti già presenti nel CRM (lookup per telefono) non richiedono OTP.
              </AlertDescription>
            </Alert>
          </DocSection>
        );

      case 'integration-google':
        return (
          <DocSection id="integration-google">
            <h3 className="text-lg font-semibold">Google Integration</h3>
            <p className="text-sm text-muted-foreground">
              Integrazione con i servizi <strong>Google</strong> (Gmail, Calendar, Contacts, Drive, Sheets, Meet) per gestire email, sincronizzare eventi e contatti direttamente con il sistema EDG VoIP.
            </p>

            <h4 className="text-sm font-semibold mt-4">Prerequisiti</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Requisito</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell>1</TableCell><TableCell>Progetto attivo su Google Cloud Console</TableCell></TableRow>
                  <TableRow><TableCell>2</TableCell><TableCell>Credenziali OAuth 2.0 configurate</TableCell></TableRow>
                  <TableRow><TableCell>3</TableCell><TableCell>API abilitate per ogni servizio utilizzato (vedi tabella sotto)</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Procedura di Setup</h4>
            <div className="space-y-2 mt-2">
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 1</Badge>Crea un nuovo progetto nella <strong>Google Cloud Console</strong> (console.cloud.google.com)</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 2</Badge>Configura la <strong>OAuth consent screen</strong> con le informazioni della tua organizzazione</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 3</Badge>Crea un <strong>OAuth 2.0 Client ID</strong> di tipo "Web application"</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 4</Badge>Aggiungi il <strong>redirect URI</strong>: <code className="bg-gray-100 px-1 rounded text-xs">https://easyflowai.it/api/integrations/google/oauth/callback</code></p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 5</Badge>Inserisci <strong>Client ID</strong> e <strong>Client Secret</strong> nelle impostazioni di integrazione EDG VoIP</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 6</Badge><strong>Abilita le API</strong> necessarie nella sezione "APIs & Services → Library" (vedi tabella sotto)</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 7</Badge>Aggiungi un <strong>account Google</strong> dal modal di integrazione e autorizza i servizi desiderati</p>
            </div>

            <h4 className="text-sm font-semibold mt-4">API da Abilitare</h4>
            <p className="text-xs text-muted-foreground mt-1 mb-2">Per ogni servizio che intendi utilizzare, devi abilitare la corrispondente API nella Google Cloud Console. Senza abilitazione, il servizio restituirà errore 403.</p>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>API</TableHead><TableHead>Servizio</TableHead><TableHead>Link diretto</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-semibold">Gmail API</TableCell><TableCell>Email (invio, ricezione, template)</TableCell><TableCell><a href="https://console.cloud.google.com/apis/api/gmail.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Abilita</a></TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Google Calendar API</TableCell><TableCell>Calendario (eventi, disponibilità)</TableCell><TableCell><a href="https://console.cloud.google.com/apis/api/calendar-json.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Abilita</a></TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">People API</TableCell><TableCell>Contatti (import/export)</TableCell><TableCell><a href="https://console.cloud.google.com/apis/api/people.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Abilita</a></TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Google Drive API</TableCell><TableCell>File e documenti</TableCell><TableCell><a href="https://console.cloud.google.com/apis/api/drive.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Abilita</a></TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Google Sheets API</TableCell><TableCell>Fogli di calcolo</TableCell><TableCell><a href="https://console.cloud.google.com/apis/api/sheets.googleapis.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Abilita</a></TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Funzionalità Supportate</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Funzionalità</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-semibold">Gmail</TableCell><TableCell>Invio email singole e massime, gestione inbox, template email</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Calendar Sync</TableCell><TableCell>Sincronizzazione eventi e disponibilità dal Google Calendar</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Contact Sync</TableCell><TableCell>Import/export contatti da Google Contacts</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Drive & Sheets</TableCell><TableCell>Accesso a file, documenti e fogli di calcolo</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Meet</TableCell><TableCell>Generazione link per videoconferenze</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">API Scopes Richiesti</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Scope</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">gmail.modify</TableCell><TableCell>Lettura, invio e gestione email</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">calendar.events</TableCell><TableCell>Lettura e scrittura eventi del calendario</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">contacts.readonly</TableCell><TableCell>Lettura contatti da Google Contacts</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">drive.readonly</TableCell><TableCell>Lettura file da Google Drive</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">spreadsheets</TableCell><TableCell>Lettura e scrittura fogli di calcolo</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Troubleshooting</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Problema</TableHead><TableHead>Soluzione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="text-sm">Errore "redirect_uri_mismatch"</TableCell><TableCell className="text-sm">Verifica che il redirect URI nelle credenziali OAuth corrisponda esattamente a quello mostrato in EDG VoIP</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Errore "access_denied"</TableCell><TableCell className="text-sm">Se l'app è in modalità Test, aggiungi l'email come utente di test nella OAuth consent screen. Altrimenti, pubblica l'app.</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Errore 403 "API not enabled"</TableCell><TableCell className="text-sm">L'API non è abilitata nel progetto Google Cloud. Usa i link nella tabella "API da Abilitare" sopra per attivarla.</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Calendar non sincronizzato</TableCell><TableCell className="text-sm">Verifica che la Calendar API sia abilitata e che lo scope calendar.events sia incluso</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Email non funzionano</TableCell><TableCell className="text-sm">Verifica che la Gmail API sia abilitata e che lo scope gmail sia stato autorizzato</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Contatti non visibili</TableCell><TableCell className="text-sm">Assicurati che la People API sia abilitata e che lo scope contacts.readonly sia stato concesso</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
          </DocSection>
        );

      case 'integration-twilio':
        return (
          <DocSection id="integration-twilio">
            <h3 className="text-lg font-semibold">Twilio Integration</h3>
            <p className="text-sm text-muted-foreground">
              Integrazione con <strong>Twilio</strong> per l'invio e la ricezione di messaggi <strong>SMS</strong> e <strong>WhatsApp</strong>, campagne SMS massivi e gestione template messages.
            </p>

            <h4 className="text-sm font-semibold mt-4">Prerequisiti</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Requisito</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell>1</TableCell><TableCell>Account Twilio attivo (twilio.com)</TableCell></TableRow>
                  <TableRow><TableCell>2</TableCell><TableCell>Account SID e Auth Token dal dashboard Twilio</TableCell></TableRow>
                  <TableRow><TableCell>3</TableCell><TableCell>Numeri di telefono acquistati o configurati su Twilio</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Procedura di Setup</h4>
            <div className="space-y-2 mt-2">
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 1</Badge>Registrati su <strong>twilio.com</strong> e completa la verifica dell'account</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 2</Badge>Recupera <strong>Account SID</strong> e <strong>Auth Token</strong> dalla dashboard principale di Twilio</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 3</Badge>Acquista o configura i <strong>numeri di telefono</strong> per SMS e/o WhatsApp</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 4</Badge>Configura il <strong>Messaging Service</strong> nel pannello Twilio (opzionale ma consigliato)</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 5</Badge>Inserisci le <strong>credenziali</strong> (Account SID, Auth Token) nelle impostazioni di integrazione EDG VoIP</p>
              <p className="text-sm"><Badge variant="outline" className="mr-2">Step 6</Badge>Configura il <strong>webhook URL</strong> per i messaggi in ingresso nel pannello Twilio</p>
            </div>

            <h4 className="text-sm font-semibold mt-4">Webhook URL per Messaggi Inbound</h4>
            <CodeBlock title="Twilio Incoming Message Webhook">{`${getPublicBaseUrl()}/api/v2/voip/webhooks/twilio/inbound`}</CodeBlock>
            <p className="text-sm text-muted-foreground mt-1">Configura questo URL nella sezione "Messaging" {">"} "A message comes in" del numero Twilio.</p>

            <h4 className="text-sm font-semibold mt-4">Funzionalità Supportate</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Funzionalità</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-semibold">SMS Sending/Receiving</TableCell><TableCell>Invio e ricezione messaggi SMS con supporto bidirezionale</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">WhatsApp Business</TableCell><TableCell>Messaggistica WhatsApp tramite Twilio WhatsApp Business API</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Campaign SMS Blast</TableCell><TableCell>Invio massivo di SMS a liste di contatti con throttling automatico</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Template Messages</TableCell><TableCell>Messaggi pre-approvati per WhatsApp con variabili dinamiche</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Rate Limits e Pricing</h4>
            <p className="text-sm text-muted-foreground">
              Twilio applica rate limits in base al tipo di account e al numero di telefono utilizzato. I messaggi SMS hanno un costo variabile per paese. WhatsApp Business utilizza un modello di pricing basato su "conversations" (24h windows). Consulta la documentazione Twilio per i dettagli aggiornati sui costi.
            </p>

            <h4 className="text-sm font-semibold mt-4">Troubleshooting</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Problema</TableHead><TableHead>Soluzione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="text-sm">Errore "Unverified number"</TableCell><TableCell className="text-sm">In modalità trial, Twilio permette l'invio solo a numeri verificati. Verifica il numero destinatario nella dashboard Twilio o esegui l'upgrade dell'account</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Messaggio non consegnato</TableCell><TableCell className="text-sm">Controlla lo stato di delivery nella sezione "Message Logs" della dashboard Twilio. Verifica che il numero mittente sia abilitato per SMS</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">Webhook non ricevuto</TableCell><TableCell className="text-sm">Verifica che il webhook URL sia configurato correttamente nel pannello Twilio e che il server EDG VoIP sia raggiungibile pubblicamente</TableCell></TableRow>
                  <TableRow><TableCell className="text-sm">WhatsApp template rifiutato</TableCell><TableCell className="text-sm">I template WhatsApp devono essere approvati da Meta. Verifica il formato e il contenuto nella sezione "Content Templates" di Twilio</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
          </DocSection>
        );

      case 'phone-provisioning':
        return (
          <DocSection id="phone-provisioning">
            <h3 className="text-lg font-semibold">Phone Provisioning</h3>
            <p className="text-sm text-muted-foreground">
              Guida alla configurazione dei telefoni fisici (Yealink, Polycom) con il sistema EDG VoIP.
              Include BLF (Busy Lamp Field) per il monitoraggio interni e Rubrica XML remota per sincronizzare i contatti CRM.
            </p>

            <h4 className="text-sm font-semibold mt-4">Funzionalità Supportate</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Feature</TableHead><TableHead>Descrizione</TableHead><TableHead>Telefoni Supportati</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-semibold">BLF (Busy Lamp Field)</TableCell><TableCell>Monitoraggio in tempo reale dello stato degli interni (libero/occupato/DND/offline)</TableCell><TableCell>Yealink T4x/T5x, Polycom VVX</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Rubrica XML Remota</TableCell><TableCell>Sincronizzazione automatica dei contatti CRM sul telefono fisico</TableCell><TableCell>Yealink (tutti i modelli), Polycom VVX</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Multi-registrazione</TableCell><TableCell>Browser + telefono fisico sulla stessa estensione con fork delle chiamate</TableCell><TableCell>Tutti</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Architettura</h4>
            <p className="text-sm text-muted-foreground">
              FreeSWITCH gestisce nativamente il <code className="bg-muted px-1 rounded">presence_id</code> per ogni interno registrato.
              I telefoni fisici si registrano via UDP/TCP e il browser SipDialpad via WebSocket (WSS).
              Quando arriva una chiamata, FreeSWITCH fa il fork a tutti i dispositivi registrati.
            </p>

            <Alert className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>DND e Queue</AlertTitle>
              <AlertDescription className="text-xs">
                DND e Queue login/logout sono gestiti a livello di estensione nel database, non per singolo dispositivo.
                Se si attiva DND dal browser, non squilla nemmeno il telefono fisico. Il DND locale del telefono fisico (tasto sulla cornetta) non viene sincronizzato con il sistema.
              </AlertDescription>
            </Alert>
          </DocSection>
        );

      case 'blf-config':
        return (
          <DocSection id="blf-config">
            <h3 className="text-lg font-semibold">BLF Configuration</h3>
            <p className="text-sm text-muted-foreground">
              Il BLF (Busy Lamp Field) permette di monitorare lo stato degli interni direttamente dai tasti del telefono fisico.
              FreeSWITCH gestisce nativamente le subscription SIP SUBSCRIBE/NOTIFY per il dialog presence.
            </p>

            <h4 className="text-sm font-semibold mt-4">API Endpoint</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/extensions</code></p>
            <Endpoint method="GET" path="/extensions/blf-list" desc="Lista interni con stato BLF e SIP URI per configurazione tasti telefono (requires voip:read)" />
            <CodeBlock title="Response Example">{`{
  "success": true,
  "data": [
    {
      "extension": "100",
      "display_name": "Mario Rossi",
      "sip_uri": "100@demo.easyflowai.it",
      "status": "available",
      "dnd": false
    },
    {
      "extension": "101",
      "display_name": "Laura Bianchi",
      "sip_uri": "101@demo.easyflowai.it",
      "status": "dnd",
      "dnd": true
    }
  ],
  "meta": { "total": 2, "sip_domain": "demo.easyflowai.it" },
  "provisioning_hint": {
    "yealink": { "line_key_type": 16, "line_key_value": "<extension>", "line_key_label": "<display_name>", "line_key_line": 1 },
    "polycom": { "attendant_type": "blf", "attendant_value": "<extension>", "attendant_label": "<display_name>" }
  }
}`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Configurazione Yealink</h4>
            <p className="text-sm text-muted-foreground">Configurare i tasti BLF sulla web interface del telefono Yealink:</p>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Parametro</TableHead><TableHead>Valore</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">Type</TableCell><TableCell className="font-mono text-xs">BLF (16)</TableCell><TableCell>Tipo tasto BLF</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">Value</TableCell><TableCell className="font-mono text-xs">100</TableCell><TableCell>Numero dell'interno da monitorare</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">Label</TableCell><TableCell className="font-mono text-xs">Mario Rossi</TableCell><TableCell>Nome visualizzato</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">Line</TableCell><TableCell className="font-mono text-xs">1</TableCell><TableCell>Linea SIP associata</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">Extension</TableCell><TableCell className="font-mono text-xs">100</TableCell><TableCell>Numero per pickup diretto</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Percorso Web Interface Yealink</h4>
            <CodeBlock>{`Phone Web UI → DSSKey → Line Key
  Key Type: BLF
  Value: <extension_number>
  Label: <display_name>
  Line: Line 1
  Extension: <extension_number>  (per directed call pickup)`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Configurazione Polycom</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Parametro</TableHead><TableHead>Valore</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">attendant.behaviors.display.spontaneousCallAppearances.runOnActive</TableCell><TableCell className="font-mono text-xs">1</TableCell><TableCell>Abilita BLF</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">attendant.resourceList.N.address</TableCell><TableCell className="font-mono text-xs">100</TableCell><TableCell>Interno da monitorare</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">attendant.resourceList.N.label</TableCell><TableCell className="font-mono text-xs">Mario Rossi</TableCell><TableCell>Nome visualizzato</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">attendant.resourceList.N.type</TableCell><TableCell className="font-mono text-xs">normal</TableCell><TableCell>Tipo di risorsa</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <h4 className="text-sm font-semibold mt-4">Auto-Provisioning via Config File</h4>
            <CodeBlock title="Yealink - Configurazione MAC.cfg">{`# BLF Keys Configuration
linekey.1.type = 16
linekey.1.value = 100
linekey.1.label = Mario Rossi
linekey.1.line = 1
linekey.1.extension = 100
linekey.1.pickup_value = *8100

linekey.2.type = 16
linekey.2.value = 101
linekey.2.label = Laura Bianchi
linekey.2.line = 1
linekey.2.extension = 101
linekey.2.pickup_value = *8101`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Stato LED BLF</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Stato LED</TableHead><TableHead>Significato</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="text-green-600 font-semibold">Verde fisso</TableCell><TableCell>Interno libero e registrato</TableCell></TableRow>
                  <TableRow><TableCell className="text-red-600 font-semibold">Rosso fisso</TableCell><TableCell>Interno occupato (in chiamata)</TableCell></TableRow>
                  <TableRow><TableCell className="text-red-600 font-semibold">Rosso lampeggiante</TableCell><TableCell>Interno che sta squillando</TableCell></TableRow>
                  <TableRow><TableCell className="text-muted-foreground font-semibold">Spento</TableCell><TableCell>Interno non registrato / offline</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
          </DocSection>
        );

      case 'phonebook-xml':
        return (
          <DocSection id="phonebook-xml">
            <h3 className="text-lg font-semibold">Remote Phonebook (XML)</h3>
            <p className="text-sm text-muted-foreground">
              Endpoint per sincronizzare i contatti CRM direttamente sul telefono fisico tramite Rubrica XML Remota.
              I telefoni Yealink e Polycom supportano nativamente questa funzionalità.
            </p>

            <h4 className="text-sm font-semibold mt-4">API Endpoints</h4>
            <p className="text-sm text-muted-foreground">Base: <code className="bg-muted px-1 rounded">/api/v2/voip/phonebook</code></p>
            <Endpoint method="GET" path="/phonebook/phonebook.xml" desc="Rubrica CRM in formato XML Yealink (requires voip:read)" />
            <Endpoint method="GET" path="/phonebook/phonebook.json" desc="Rubrica CRM in formato JSON (requires voip:read)" />

            <h4 className="text-sm font-semibold mt-4">Query Parameters</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Param</TableHead><TableHead>Tipo</TableHead><TableHead>Default</TableHead><TableHead>Descrizione</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-mono text-xs">type</TableCell><TableCell>string</TableCell><TableCell>all</TableCell><TableCell>Filtro tipo: <code className="bg-muted px-1 rounded">all</code>, <code className="bg-muted px-1 rounded">customers</code>, <code className="bg-muted px-1 rounded">leads</code>, <code className="bg-muted px-1 rounded">extensions</code></TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">search</TableCell><TableCell>string</TableCell><TableCell>-</TableCell><TableCell>Ricerca per nome, cognome, azienda o telefono</TableCell></TableRow>
                  <TableRow><TableCell className="font-mono text-xs">limit</TableCell><TableCell>number</TableCell><TableCell>1000</TableCell><TableCell>Limite massimo contatti (max 5000)</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>

            <CodeBlock title="XML Response Example">{`<?xml version="1.0" encoding="UTF-8"?>
<YealinkIPPhoneDirectory>
  <Title>EDG VoIP Rubrica</Title>
  <Prompt>Rubrica CRM</Prompt>
  <DirectoryEntry>
    <Name>Mario Rossi</Name>
    <Telephone>+39123456789</Telephone>
    <Telephone>+39987654321</Telephone>
  </DirectoryEntry>
  <DirectoryEntry>
    <Name>Acme Corp (Laura Bianchi)</Name>
    <Telephone>+39111222333</Telephone>
  </DirectoryEntry>
  <DirectoryEntry>
    <Name>Int. 100 - Reception</Name>
    <Telephone>100</Telephone>
  </DirectoryEntry>
</YealinkIPPhoneDirectory>`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Configurazione Yealink</h4>
            <p className="text-sm text-muted-foreground">Configurare la Rubrica Remota sulla web interface del telefono:</p>
            <CodeBlock title="Percorso Web UI">{`Phone Web UI → Directory → Remote Phone Book
  Remote URL: ${getPublicBaseUrl()}/api/v2/voip/phonebook/phonebook.xml
  Display Name: Rubrica CRM

HTTP Headers (nella sezione Settings → Auto Provision → HTTP):
  X-API-Key: sk_live_your_api_key_here`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Auto-Provisioning via Config File</h4>
            <CodeBlock title="Yealink - Configurazione MAC.cfg">{`# Remote Phonebook
remote_phonebook.data.1.url = ${getPublicBaseUrl()}/api/v2/voip/phonebook/phonebook.xml
remote_phonebook.data.1.name = Rubrica CRM
remote_phonebook.data.2.url = ${getPublicBaseUrl()}/api/v2/voip/phonebook/phonebook.xml?type=extensions
remote_phonebook.data.2.name = Interni

# Aggiornamento automatico ogni 3600 secondi (1 ora)
remote_phonebook.data.1.refresh_time = 3600`}</CodeBlock>

            <h4 className="text-sm font-semibold mt-4">Configurazione Polycom</h4>
            <CodeBlock title="Polycom - Provisioning">{`# Corporate Directory (Polycom VVX)
dir.corp.address = ${getPublicBaseUrl()}/api/v2/voip/phonebook/phonebook.xml
dir.corp.attribute.1.name = "X-API-Key"
dir.corp.attribute.1.value = "sk_live_your_api_key_here"
dir.corp.backGroundSync = 1
dir.corp.backGroundSync.period = 3600`}</CodeBlock>

            <Alert className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Autenticazione</AlertTitle>
              <AlertDescription className="text-xs">
                L'endpoint richiede una API Key valida con scope <code className="bg-muted px-1 rounded">voip:read</code>.
                Configura l'header <code className="bg-muted px-1 rounded">X-API-Key</code> nel provisioning del telefono.
                Non tutti i telefoni supportano header personalizzati — in quel caso usa il parametro query <code className="bg-muted px-1 rounded">?apikey=sk_live_...</code> (meno sicuro).
              </AlertDescription>
            </Alert>

            <h4 className="text-sm font-semibold mt-4">Confronto Browser vs Telefono Fisico</h4>
            <div className="border rounded-md overflow-hidden mt-2">
              <Table>
                <TableHeader><TableRow><TableHead>Feature</TableHead><TableHead>Browser (SipDialpad)</TableHead><TableHead>Telefono Fisico</TableHead></TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell className="font-semibold">Chiamate in ingresso</TableCell><TableCell>Squilla</TableCell><TableCell>Squilla (fork)</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">DND</TableCell><TableCell>Controlla DB = vale per tutti</TableCell><TableCell>DND locale non sincronizzato</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Queue login/logout</TableCell><TableCell>Controlla DB = vale per tutti</TableCell><TableCell>Non supportato</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Contatti/CRM</TableCell><TableCell>Popup riconoscimento</TableCell><TableCell>Rubrica XML remota</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">BLF</TableCell><TableCell>Switchboard UI</TableCell><TableCell>LED fisici sui tasti</TableCell></TableRow>
                  <TableRow><TableCell className="font-semibold">Registrazione</TableCell><TableCell>WebSocket (WSS)</TableCell><TableCell>UDP/TCP</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
          </DocSection>
        );

    default:
      return null;
  }
}
